import React, { useEffect, useState } from 'react';
import { WamerceShell } from './WamerceDashboardV2';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  MessageCircle, ShieldCheck, FileText, Send, Webhook, RefreshCw, Copy,
  CheckCircle2, AlertTriangle, Clock, Trash2, Plus, Play, Zap, X, Workflow,
  Banknote, Timer, XCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const bizId = () => (typeof window === 'undefined' ? 'default' : localStorage.getItem('wamerce-business') || 'default');
const apiFetch = (path, opts = {}) =>
  fetch(`${API}${path}`, {
    ...opts,
    headers: { 'X-Wamerce-Business-Id': bizId(), ...(opts.headers || {}) },
  });

const STATUS_TINT = {
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  PENDING: 'bg-amber-100 text-amber-700 border-amber-300',
  IN_APPEAL: 'bg-violet-100 text-violet-700 border-violet-300',
  REJECTED: 'bg-rose-100 text-rose-700 border-rose-300',
  PAUSED: 'bg-slate-100 text-slate-600 border-slate-300',
  DISABLED: 'bg-slate-100 text-slate-500 border-slate-300',
  ALREADY_EXISTS: 'bg-sky-100 text-sky-700 border-sky-300',
};
const CAT_TINT = {
  UTILITY: 'bg-sky-50 text-sky-700 border-sky-200',
  MARKETING: 'bg-rose-50 text-rose-700 border-rose-200',
  AUTHENTICATION: 'bg-violet-50 text-violet-700 border-violet-200',
};

export default function WamerceWhatsApp() {
  const [tab, setTab] = useState('templates');
  const TABS = [
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'library', label: 'Template Library', icon: Zap },
    { id: 'automations', label: 'Automations', icon: Workflow },
    { id: 'cod', label: 'COD Auto-Cancel', icon: Banknote },
    { id: 'webhook', label: 'Webhook & Testing', icon: Webhook },
    { id: 'send', label: 'Send Test Message', icon: Send },
  ];

  return (
    <WamerceShell active="whatsapp">
      <header className="space-y-1 mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-11 h-11 rounded-2xl bg-emerald-500/10 items-center justify-center">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
          </span>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">WhatsApp Console</h1>
            <p className="text-slate-500 wamerce-dark:text-slate-400">Manage templates, test webhooks, and send messages via Meta Cloud API.</p>
          </div>
        </div>
      </header>

      <nav className="flex items-center gap-1 mb-6 border-b border-slate-200 wamerce-dark:border-slate-800 overflow-x-auto" data-testid="wa-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`wa-tab-${t.id}`}
              className={`h-11 px-4 inline-flex items-center gap-2 text-sm font-medium border-b-2 transition-colors ${
                isActive ? 'border-emerald-500 text-emerald-700 wamerce-dark:text-emerald-300'
                         : 'border-transparent text-slate-500 hover:text-slate-800 wamerce-dark:hover:text-slate-200'
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === 'templates' && <TemplatesTab />}
      {tab === 'library' && <LibraryTab />}
      {tab === 'automations' && <AutomationsTab />}
      {tab === 'cod' && <CodTab />}
      {tab === 'webhook' && <WebhookTab />}
      {tab === 'send' && <SendTab />}
    </WamerceShell>
  );
}

// =================== TEMPLATES TAB ===================
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [source, setSource] = useState('meta');

  const load = () => {
    setLoading(true);
    apiFetch('/api/v2/whatsapp/templates')
      .then((r) => r.json())
      .then((d) => { setItems(d.templates || []); setSource(d.source || 'meta'); if (d.warning) toast.warning(d.warning); })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (name) => {
    if (!window.confirm(`Delete template "${name}" from Meta? This cannot be undone.`)) return;
    try {
      const r = await apiFetch(`/api/v2/whatsapp/templates/${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json()).detail || 'Delete failed');
      toast.success(`Deleted ${name}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">
          <span className="font-semibold text-slate-700 wamerce-dark:text-slate-300">{items.length}</span> templates
          {source === 'cache' && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">cached</span>}
          {source === 'meta' && <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">live from Meta</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="wa-templates-refresh" className="h-9">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="wa-templates-new" className="h-9 bg-emerald-500 hover:bg-emerald-400 text-white">
            <Plus className="w-4 h-4 mr-1" /> New template
          </Button>
        </div>
      </div>

      {showForm && <CreateTemplateForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 overflow-hidden">
        {loading ? (
          <p className="p-10 text-center text-slate-500">Loading templates from Meta…</p>
        ) : items.length === 0 ? (
          <div className="p-10 text-center space-y-3">
            <FileText className="w-10 h-10 mx-auto text-slate-300" />
            <p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">No templates yet</p>
            <p className="text-sm text-slate-500">Submit your first template or seed the pre-built library.</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 wamerce-dark:bg-slate-800/40">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Category</th>
                <th className="text-left px-5 py-3 font-medium">Language</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Reason</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const status = (t.status || 'PENDING').toUpperCase();
                const tint = STATUS_TINT[status] || STATUS_TINT.PENDING;
                return (
                  <tr key={`${t.name}-${t.language}`} data-testid={`tpl-row-${t.name}`}
                      className="border-t border-slate-100 wamerce-dark:border-slate-800/60 hover:bg-slate-50/50 wamerce-dark:hover:bg-slate-800/30">
                    <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-900 wamerce-dark:text-white">{t.name}</td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-semibold ${CAT_TINT[t.category] || ''}`}>{t.category}</span></td>
                    <td className="px-5 py-3 text-slate-500">{t.language}</td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-bold ${tint}`}>{status}</span></td>
                    <td className="px-5 py-3 text-xs text-slate-500 max-w-[280px] truncate" title={t.rejected_reason || ''}>{t.rejected_reason || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <button type="button" onClick={() => onDelete(t.name)} data-testid={`tpl-del-${t.name}`}
                        className="text-rose-500 hover:text-rose-700 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  );
}

function CreateTemplateForm({ onClose, onCreated }) {
  const [data, setData] = useState({ name: '', category: 'UTILITY', language: 'en', body_text: '', header_text: '', footer_text: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!data.name || !data.body_text) { toast.error('Name and body are required'); return; }
    if (!/^[a-z0-9_]+$/.test(data.name)) { toast.error('Name must be lowercase letters, digits, underscores'); return; }
    setSubmitting(true);
    try {
      const r = await apiFetch('/api/v2/whatsapp/templates', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Submission failed');
      toast.success(`Submitted "${data.name}" — Meta will review within ~24h`);
      onCreated();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-5 space-y-4" data-testid="wa-template-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Submit new template</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Name (lowercase_underscores)</label>
          <Input data-testid="tpl-name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="order_confirmation" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Category</label>
          <select data-testid="tpl-category" value={data.category} onChange={(e) => setData({ ...data, category: e.target.value })}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700">
            <option value="UTILITY">UTILITY (transactional)</option>
            <option value="MARKETING">MARKETING (promotional)</option>
            <option value="AUTHENTICATION">AUTHENTICATION (OTP)</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Language</label>
          <select data-testid="tpl-lang" value={data.language} onChange={(e) => setData({ ...data, language: e.target.value })}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700">
            <option value="en">English</option>
            <option value="en_US">English (US)</option>
            <option value="hi">Hindi</option>
            <option value="ur">Urdu</option>
            <option value="ar">Arabic</option>
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">Header text (optional)</label>
        <Input data-testid="tpl-header" value={data.header_text} onChange={(e) => setData({ ...data, header_text: e.target.value })} placeholder="Order Confirmed ✅" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">Body (use {'{{1}}'}, {'{{2}}'} for variables) *</label>
        <textarea data-testid="tpl-body" value={data.body_text} onChange={(e) => setData({ ...data, body_text: e.target.value })} rows={5}
          placeholder="Hi {{1}}, your order #{{2}} for ₹{{3}} is confirmed."
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700 resize-none" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">Footer text (optional)</label>
        <Input data-testid="tpl-footer" value={data.footer_text} onChange={(e) => setData({ ...data, footer_text: e.target.value })} placeholder="Reply STOP to unsubscribe" />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 wamerce-dark:border-slate-800">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={submitting} data-testid="tpl-submit" className="bg-emerald-500 hover:bg-emerald-400 text-white">
          {submitting ? 'Submitting…' : 'Submit for Meta approval'}
        </Button>
      </div>
    </div>
  );
}

// =================== LIBRARY TAB ===================
function LibraryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    apiFetch('/api/v2/whatsapp/template-library').then((r) => r.json())
      .then((d) => setItems(d.library || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const seedAll = async () => {
    const names = selected.size > 0 ? Array.from(selected) : null;
    if (!window.confirm(`Submit ${names ? names.length : items.length} template(s) to Meta for approval?`)) return;
    setSeeding(true);
    try {
      const r = await apiFetch('/api/v2/whatsapp/template-library/seed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ only: names }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Seed failed');
      toast.success(`Submitted ${d.submitted}/${d.results?.length || 0}. ${d.failed > 0 ? `${d.failed} failed.` : ''}`);
      if (d.failed > 0) console.error('Failed templates:', d.results.filter((x) => !x.ok));
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e.message); } finally { setSeeding(false); }
  };

  const toggle = (name) => {
    const next = new Set(selected);
    next.has(name) ? next.delete(name) : next.add(name);
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-sky-50 wamerce-dark:from-emerald-500/10 wamerce-dark:to-sky-500/10 border border-emerald-200 wamerce-dark:border-emerald-500/30 p-5">
        <div className="flex items-start gap-4">
          <span className="inline-flex w-10 h-10 rounded-xl bg-emerald-500 items-center justify-center shrink-0"><Zap className="w-5 h-5 text-white" /></span>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Pre-built Meta-compliant template library</h3>
            <p className="text-sm text-slate-600 wamerce-dark:text-slate-400 mt-1">
              {items.length} ready-to-submit templates covering order lifecycle, marketing, and authentication.
              Pick the ones you want and submit them to Meta in one click.
            </p>
          </div>
          <Button onClick={seedAll} disabled={seeding || loading} data-testid="library-seed-all"
            className="h-10 bg-emerald-500 hover:bg-emerald-400 text-white">
            <Play className="w-4 h-4 mr-1" />
            {seeding ? 'Submitting…' : selected.size > 0 ? `Submit ${selected.size} selected` : 'Submit all to Meta'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-10 text-slate-500">Loading library…</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => {
            const sub = t.submission;
            const isSelected = selected.has(t.name);
            return (
              <div key={t.name} data-testid={`lib-${t.name}`}
                onClick={() => toggle(t.name)}
                className={`rounded-2xl bg-white wamerce-dark:bg-[#161826] border-2 p-5 space-y-3 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  isSelected ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-slate-200 wamerce-dark:border-slate-800'
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-sm font-bold text-slate-900 wamerce-dark:text-white font-mono">{t.name}</code>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${CAT_TINT[t.category]}`}>{t.category}</span>
                      {sub && <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${STATUS_TINT[(sub.status || '').toUpperCase()] || STATUS_TINT.PENDING}`}>{sub.status}</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                  </div>
                  <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4 accent-emerald-500 mt-1" />
                </div>
                {t.header_text && (
                  <div className="text-xs bg-slate-50 wamerce-dark:bg-slate-800/40 rounded p-2 font-semibold text-slate-700 wamerce-dark:text-slate-300">
                    HEADER: {t.header_text}
                  </div>
                )}
                <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 wamerce-dark:bg-slate-800/40 rounded p-3 text-slate-700 wamerce-dark:text-slate-300 font-sans">{t.body_text}</pre>
                {t.footer_text && <p className="text-[11px] text-slate-400 italic">{t.footer_text}</p>}
                {t.buttons?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {t.buttons.map((b, i) => (
                      <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md bg-sky-50 text-sky-700 text-xs font-semibold border border-sky-200">
                        {b.text} {b.type === 'URL' && '↗'} {b.type === 'PHONE_NUMBER' && '☎'}
                      </span>
                    ))}
                  </div>
                )}
                {t.variables?.length > 0 && (
                  <div className="text-[11px] text-slate-500"><span className="font-semibold">Variables:</span> {t.variables.map((v, i) => `{{${i+1}}}=${v}`).join(', ')}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =================== WEBHOOK TAB ===================
function WebhookTab() {
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('Test inbound message from Wamerce dashboard');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadConfig = () => apiFetch('/api/v2/whatsapp/webhook-config').then((r) => r.json()).then(setConfig).catch(() => {});
  const loadEvents = () => apiFetch('/api/v2/whatsapp/webhook-events?limit=50').then((r) => r.json()).then((d) => setEvents(d.events || [])).catch(() => {});
  useEffect(() => { loadConfig(); loadEvents(); const t = setInterval(loadEvents, 10000); return () => clearInterval(t); }, []);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied to clipboard'); };

  const sendTest = async () => {
    if (!phone.trim()) { toast.error('Enter a phone number to test from'); return; }
    setSending(true);
    try {
      const r = await apiFetch('/api/v2/whatsapp/webhook-test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_phone: phone, text }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Test failed');
      toast.success(`Simulated message from +${d.phone}. Check Inbox.`);
      loadEvents();
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  const fmtTime = (s) => s ? new Date(s * 1000).toLocaleString() : '—';

  return (
    <div className="space-y-5">
      {/* Webhook config */}
      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-5 space-y-4" data-testid="wa-webhook-config">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-xl bg-violet-500/10 items-center justify-center"><ShieldCheck className="w-5 h-5 text-violet-600" /></span>
          <div>
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Webhook configuration</h3>
            <p className="text-xs text-slate-500">Paste these into Meta App Dashboard → WhatsApp → Configuration → Webhook</p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold">
            {config?.configured ? (
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Configured</span>
            ) : (
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300"><AlertTriangle className="w-3 h-3 inline mr-1" /> Not configured</span>
            )}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Callback URL</label>
            <div className="flex items-center gap-2 rounded-md border border-slate-200 wamerce-dark:border-slate-700 bg-slate-50 wamerce-dark:bg-slate-800/60 px-3 h-10">
              <code className="flex-1 text-xs font-mono text-slate-800 wamerce-dark:text-slate-200 truncate" data-testid="webhook-url">{config?.webhook_url || '—'}</code>
              <button type="button" onClick={() => copy(config?.webhook_url || '')} className="text-slate-400 hover:text-slate-700"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Verify Token</label>
            <div className="flex items-center gap-2 rounded-md border border-slate-200 wamerce-dark:border-slate-700 bg-slate-50 wamerce-dark:bg-slate-800/60 px-3 h-10">
              <code className="flex-1 text-xs font-mono text-slate-800 wamerce-dark:text-slate-200 truncate" data-testid="verify-token">{config?.verify_token || '—'}</code>
              <button type="button" onClick={() => copy(config?.verify_token || '')} className="text-slate-400 hover:text-slate-700"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Phone Number ID</label>
            <code className="block text-xs font-mono text-slate-700 wamerce-dark:text-slate-300 bg-slate-50 wamerce-dark:bg-slate-800/60 rounded px-3 py-2.5">{config?.phone_number_id || '—'}</code>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">WhatsApp Business Account ID</label>
            <code className="block text-xs font-mono text-slate-700 wamerce-dark:text-slate-300 bg-slate-50 wamerce-dark:bg-slate-800/60 rounded px-3 py-2.5">{config?.waba_id || '—'}</code>
          </div>
        </div>
        {config?.instructions && (
          <ol className="space-y-1.5 text-sm text-slate-600 wamerce-dark:text-slate-300 list-none pl-0 mt-2 bg-slate-50/60 wamerce-dark:bg-slate-800/30 rounded-lg p-4">
            {config.instructions.map((it, i) => <li key={i} className="flex gap-2"><span className="text-emerald-500 font-bold">→</span>{it}</li>)}
          </ol>
        )}
      </section>

      {/* Send test webhook */}
      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-5 space-y-4" data-testid="wa-webhook-test">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-xl bg-emerald-500/10 items-center justify-center"><Play className="w-5 h-5 text-emerald-600" /></span>
          <div>
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Simulate inbound message</h3>
            <p className="text-xs text-slate-500">Test your inbox + webhook plumbing without sending from a real phone.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-[1fr_2fr_auto] gap-3">
          <Input data-testid="test-phone" placeholder="From phone (e.g. 919876543210)" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Input data-testid="test-text" placeholder="Message text" value={text} onChange={(e) => setText(e.target.value)} />
          <Button onClick={sendTest} disabled={sending} data-testid="test-send" className="bg-emerald-500 hover:bg-emerald-400 text-white whitespace-nowrap">
            <Play className="w-4 h-4 mr-1" /> Send Test
          </Button>
        </div>
      </section>

      {/* Event log */}
      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 overflow-hidden">
        <header className="p-5 flex items-center justify-between border-b border-slate-100 wamerce-dark:border-slate-800">
          <div>
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Recent webhook events</h3>
            <p className="text-xs text-slate-500">Auto-refreshes every 10s · last 50 events from Meta</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadEvents} className="h-9"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </header>
        {events.length === 0 ? (
          <div className="p-10 text-center space-y-2">
            <Clock className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-sm text-slate-500">No webhook events received yet. Send a test message above or message your WA Business number.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 wamerce-dark:divide-slate-800 max-h-[500px] overflow-y-auto">
            {events.map((e, i) => {
              const isOpen = expanded === i;
              const isTest = e.source === 'wamerce-test';
              const firstMsg = e.payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
              const firstStatus = e.payload?.entry?.[0]?.changes?.[0]?.value?.statuses?.[0];
              const summary = firstMsg
                ? `📩 ${firstMsg.from} · "${firstMsg.text?.body || firstMsg.type}"`
                : firstStatus
                  ? `📤 status=${firstStatus.status} · ${firstStatus.recipient_id || '—'}`
                  : `↺ ${e.object}`;
              return (
                <li key={i} data-testid={`event-${i}`}>
                  <button type="button" onClick={() => setExpanded(isOpen ? null : i)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/40 text-left">
                    <span className={`w-2 h-2 rounded-full ${isTest ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 wamerce-dark:text-slate-200 truncate">{summary}</p>
                      <p className="text-[11px] text-slate-500">{fmtTime(e.received_at)} {isTest && '· simulated'}</p>
                    </div>
                    <span className="text-xs text-slate-400">{isOpen ? '▾' : '▸'}</span>
                  </button>
                  {isOpen && (
                    <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-slate-900 text-emerald-200 p-4 max-h-[300px] overflow-y-auto">{JSON.stringify(e.payload, null, 2)}</pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

// =================== SEND TAB ===================
function SendTab() {
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState('template');
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [tplName, setTplName] = useState('');
  const [lang, setLang] = useState('en');
  const [varsStr, setVarsStr] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    apiFetch('/api/v2/whatsapp/templates').then((r) => r.json())
      .then((d) => {
        const approved = (d.templates || []).filter((t) => (t.status || '').toUpperCase() === 'APPROVED');
        setTemplates(approved);
        if (approved[0]) { setTplName(approved[0].name); setLang(approved[0].language || 'en'); }
      }).catch(() => {});
  }, []);

  const send = async () => {
    if (!phone.trim()) { toast.error('Enter a recipient phone'); return; }
    setSending(true);
    try {
      let r, d;
      if (mode === 'template') {
        if (!tplName) { toast.error('Pick a template'); setSending(false); return; }
        const vars = varsStr.split('|').map((s) => s.trim()).filter(Boolean);
        r = await apiFetch('/api/v2/whatsapp/send-template', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: phone, template_name: tplName, language: lang, variables: vars }),
        });
      } else {
        if (!text.trim()) { toast.error('Enter message text'); setSending(false); return; }
        r = await apiFetch('/api/v2/whatsapp/send-text', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: phone, text }),
        });
      }
      d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Sent to +${d.to} · ${d.message_id?.slice(-8)}`);
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  return (
    <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-6 space-y-5 max-w-3xl" data-testid="wa-send-form">
      <div className="flex items-center gap-3">
        <span className="inline-flex w-10 h-10 rounded-xl bg-sky-500/10 items-center justify-center"><Send className="w-5 h-5 text-sky-600" /></span>
        <div>
          <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Send WhatsApp message</h3>
          <p className="text-xs text-slate-500">Use templates for first contact (24h+ old). Free-text only inside the 24h customer-service window.</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {['template', 'text'].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`mode-${m}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold ${mode === m ? 'bg-emerald-500 text-white' : 'bg-slate-100 wamerce-dark:bg-slate-800 text-slate-600'}`}>
            {m === 'template' ? 'Template' : 'Free text (24h window)'}
          </button>
        ))}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">Recipient phone (with country code, no +)</label>
        <Input data-testid="send-phone" placeholder="919876543210" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>

      {mode === 'template' ? (
        <>
          <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Approved template</label>
              <select data-testid="send-template-name" value={tplName} onChange={(e) => setTplName(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700">
                {templates.length === 0 ? <option value="">— no approved templates —</option> :
                  templates.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.category})</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Language</label>
              <Input data-testid="send-lang" value={lang} onChange={(e) => setLang(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Variables (separated by | — e.g. <code>Sunny|TNV1234|999</code>)</label>
            <Input data-testid="send-vars" placeholder="Var1|Var2|Var3" value={varsStr} onChange={(e) => setVarsStr(e.target.value)} />
          </div>
        </>
      ) : (
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Message text</label>
          <textarea data-testid="send-text-body" rows={5} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Hello! This is a reply within the 24h customer service window."
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700 resize-none" />
        </div>
      )}

      <div className="flex items-center justify-end pt-3 border-t border-slate-100 wamerce-dark:border-slate-800">
        <Button onClick={send} disabled={sending} data-testid="send-now-btn" className="bg-emerald-500 hover:bg-emerald-400 text-white h-10 px-5">
          <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Sending…' : 'Send now'}
        </Button>
      </div>
    </section>
  );
}

// =================== AUTOMATIONS TAB ===================
function AutomationsTab() {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [testPhone, setTestPhone] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch('/api/v2/automations').then((r) => r.json()),
      apiFetch('/api/v2/automations/logs?limit=20').then((r) => r.json()),
      apiFetch('/api/v2/whatsapp/templates').then((r) => r.json()),
    ]).then(([r, l, t]) => {
      setRules(r.rules || []);
      setLogs(l.logs || []);
      setTemplates((t.templates || []).filter((x) => (x.status || '').toUpperCase() === 'APPROVED'));
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(() => { apiFetch('/api/v2/automations/logs?limit=20').then((r) => r.json()).then((l) => setLogs(l.logs || [])); }, 15000); return () => clearInterval(t); }, []);

  const saveRule = async (rule, patch) => {
    const next = { ...rule, ...patch };
    setRules((rs) => rs.map((x) => x.event_type === rule.event_type ? next : x));
    try {
      const r = await apiFetch(`/api/v2/automations/${rule.event_type}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next.enabled, template_name: next.template_name, language: next.language || 'en', delay_seconds: next.delay_seconds || 0 }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      toast.success(`${rule.label} ${next.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e.message); load(); }
  };

  const test = async (rule) => {
    if (!testPhone.trim()) { toast.error('Enter a test phone number first'); return; }
    setTesting(rule.event_type);
    try {
      const r = await apiFetch('/api/v2/automations/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: rule.event_type, phone: testPhone }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.detail || d.reason || 'Test failed');
      toast.success(`Sent! Message ID: ${(d.message_id || '').slice(-10)}`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.message); } finally { setTesting(null); }
  };

  const fmtTime = (s) => s ? new Date(s * 1000).toLocaleString() : '—';
  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-emerald-50 wamerce-dark:from-violet-500/10 wamerce-dark:to-emerald-500/10 border border-violet-200 wamerce-dark:border-violet-500/30 p-5 flex items-start gap-4">
        <span className="inline-flex w-10 h-10 rounded-xl bg-violet-500 items-center justify-center shrink-0"><Workflow className="w-5 h-5 text-white" /></span>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Shopify → WhatsApp Automations</h3>
          <p className="text-sm text-slate-600 wamerce-dark:text-slate-400 mt-1">
            Auto-send approved templates when Shopify events fire. <span className="font-semibold text-emerald-700">{enabledCount}/{rules.length} active.</span>
            Connect your Shopify webhooks (orders/create, orders/paid, orders/fulfilled) to <code className="px-1.5 py-0.5 rounded bg-slate-100 text-xs font-mono">/api/shopify/webhook/*</code> — these triggers are already live.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <label className="text-xs font-semibold text-slate-600">Test phone:</label>
          <Input data-testid="auto-test-phone" placeholder="919876543210" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} className="h-9 w-44 text-sm" />
        </div>
      </div>

      {loading ? <p className="text-center py-10 text-slate-500">Loading rules…</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {rules.map((rule) => (
            <RuleCard key={rule.event_type} rule={rule} templates={templates}
              onSave={(patch) => saveRule(rule, patch)} onTest={() => test(rule)} testing={testing === rule.event_type} />
          ))}
        </div>
      )}

      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 overflow-hidden mt-5">
        <header className="p-5 border-b border-slate-100 wamerce-dark:border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Recent Automation Sends</h3>
            <p className="text-xs text-slate-500">Auto-refreshes every 15s · last 20 events</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="h-9"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </header>
        {logs.length === 0 ? (
          <div className="p-10 text-center space-y-2">
            <Clock className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-sm text-slate-500">No automation sends yet. Enable a rule above, then place a test order on Shopify.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 wamerce-dark:bg-slate-800/40">
              <tr>
                <th className="text-left px-5 py-3 font-medium">When</th>
                <th className="text-left px-5 py-3 font-medium">Event</th>
                <th className="text-left px-5 py-3 font-medium">Template</th>
                <th className="text-left px-5 py-3 font-medium">Phone</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={i} data-testid={`log-${i}`} className="border-t border-slate-100 wamerce-dark:border-slate-800/60">
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtTime(l.sent_at)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-800">{l.event_type}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{l.template_name || '—'}</td>
                  <td className="px-5 py-3 text-slate-600">+{l.phone || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-semibold ${
                      l.status === 'sent' ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                          : 'bg-rose-100 text-rose-700 border-rose-300'
                    }`} title={l.status}>{l.status === 'sent' ? '✓ sent' : (l.status || '').slice(0, 24)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function RuleCard({ rule, templates, onSave, onTest, testing }) {
  return (
    <div className={`rounded-2xl bg-white wamerce-dark:bg-[#161826] border-2 p-5 space-y-3 transition-all ${
      rule.enabled ? 'border-emerald-300 shadow-sm shadow-emerald-100/40' : 'border-slate-200 wamerce-dark:border-slate-800'
    }`} data-testid={`rule-${rule.event_type}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900 wamerce-dark:text-white">{rule.label}</h4>
            <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{rule.shopify_topic}</code>
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{rule.description}</p>
        </div>
        <button type="button" onClick={() => onSave({ enabled: !rule.enabled })} data-testid={`toggle-${rule.event_type}`}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
            rule.enabled ? 'bg-emerald-500' : 'bg-slate-200'
          }`}>
          <span className={`inline-block w-4 h-4 transform rounded-full bg-white shadow transition-transform ${
            rule.enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template</label>
        <select value={rule.template_name} onChange={(e) => onSave({ template_name: e.target.value })}
          data-testid={`tpl-${rule.event_type}`}
          className="w-full h-9 rounded-md border border-slate-200 bg-slate-50 wamerce-dark:bg-slate-800/60 wamerce-dark:border-slate-700 px-2.5 text-sm text-slate-900 wamerce-dark:text-white">
          {templates.length === 0 && <option value={rule.template_name}>{rule.template_name} (no approved templates)</option>}
          {templates.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.category})</option>)}
        </select>
      </div>

      <div className="rounded-lg bg-slate-50/80 wamerce-dark:bg-slate-800/30 p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Variable mapping</p>
        <ul className="text-[11px] text-slate-600 wamerce-dark:text-slate-400 space-y-0.5 font-mono">
          {rule.variables_doc.map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      </div>

      <Button onClick={onTest} disabled={testing || !rule.enabled} variant="outline" size="sm"
        data-testid={`test-${rule.event_type}`}
        className="w-full h-9 border-emerald-400 text-emerald-700 hover:bg-emerald-50">
        <Play className="w-3.5 h-3.5 mr-1" /> {testing ? 'Sending…' : 'Test this rule'}
      </Button>
    </div>
  );
}


// =================== COD AUTO-CANCEL TAB ===================
function CodTab() {
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, cancelled: 0, cancel_failed: 0, total: 0, settings: {} });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch('/api/v2/cod/stats').then((r) => r.json()),
      apiFetch(`/api/v2/cod/confirmations?limit=100${filter ? `&status=${filter}` : ''}`).then((r) => r.json()),
    ]).then(([s, l]) => {
      setStats(s);
      setItems(l.items || []);
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [filter]);

  const saveSetting = async (patch) => {
    const next = { ...(stats.settings || {}), ...patch };
    setStats((s) => ({ ...s, settings: next }));
    try {
      const r = await apiFetch('/api/v2/cod/settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: next.enabled ?? true,
          auto_cancel_hours: next.auto_cancel_hours ?? 24,
          cancel_reason: next.cancel_reason || 'customer',
          cancel_note: next.cancel_note || '',
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      toast.success('Saved');
    } catch (e) { toast.error(e.message); load(); }
  };

  const runCleanup = async () => {
    if (!window.confirm('Manually scan and cancel any pending COD confirmations older than the threshold?')) return;
    setRunning(true);
    try {
      const r = await apiFetch('/api/v2/cod/run-cleanup', { method: 'POST' });
      const d = await r.json();
      toast.success(`Checked ${d.checked} · Cancelled ${d.cancelled} · Failed ${d.failed}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setRunning(false); }
  };

  const manualCancel = async (orderId) => {
    if (!window.confirm(`Cancel order #${orderId} on Shopify now?`)) return;
    try {
      const r = await apiFetch(`/api/v2/cod/cancel/${encodeURIComponent(orderId)}`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) toast.success(`Cancelled order #${orderId}`);
      else toast.error(d.reason || d.body?.slice(0, 100) || 'Cancel failed');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const fmtTime = (s) => s ? new Date(s * 1000).toLocaleString() : '—';
  const sinceHrs = (s) => s ? Math.round((Date.now()/1000 - s) / 360) / 10 : null;

  const STATUS_PILL = {
    pending: 'bg-amber-100 text-amber-700 border-amber-300',
    confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    cancelled: 'bg-rose-100 text-rose-700 border-rose-300',
    cancel_failed: 'bg-slate-200 text-slate-700 border-slate-300',
  };

  return (
    <div className="space-y-5">
      {/* Hero / Settings */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-rose-50 wamerce-dark:from-amber-500/10 wamerce-dark:to-rose-500/10 border border-amber-200 wamerce-dark:border-amber-500/30 p-5 space-y-4">
        <div className="flex items-start gap-4 flex-wrap">
          <span className="inline-flex w-11 h-11 rounded-2xl bg-amber-500 items-center justify-center shrink-0"><Banknote className="w-5 h-5 text-white" /></span>
          <div className="flex-1 min-w-[250px]">
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">COD Auto-Confirm &amp; Cancel</h3>
            <p className="text-sm text-slate-600 wamerce-dark:text-slate-400 mt-1">
              When a new COD order is placed, we send <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">cod_confirmation</code> on WhatsApp.
              Reply <strong>YES</strong> to confirm, <strong>NO</strong> to cancel. Unconfirmed orders are auto-cancelled after the threshold.
              <span className="block mt-1 text-xs text-amber-700 wamerce-dark:text-amber-300 font-semibold">Detects: yes/no, haan/nahi, हाँ/नहीं, جی/نہیں, نعم/لا</span>
            </p>
          </div>
          <Button onClick={runCleanup} disabled={running} data-testid="cod-run-cleanup" className="h-10 bg-amber-500 hover:bg-amber-400 text-white whitespace-nowrap">
            <Play className="w-4 h-4 mr-1.5" /> {running ? 'Running…' : 'Run sweep now'}
          </Button>
        </div>

        <div className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end pt-3 border-t border-amber-200/60">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Auto-cancel after</label>
            <div className="flex items-center gap-2">
              <Input data-testid="cod-hours" type="number" min={1} max={720}
                value={stats.settings?.auto_cancel_hours ?? 24}
                onChange={(e) => saveSetting({ auto_cancel_hours: parseInt(e.target.value || '24') })} className="h-9 w-24" />
              <span className="text-sm text-slate-600">hours</span>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Cancel reason</label>
            <select data-testid="cod-reason"
              value={stats.settings?.cancel_reason || 'customer'}
              onChange={(e) => saveSetting({ cancel_reason: e.target.value })}
              className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white">
              <option value="customer">customer</option>
              <option value="declined">declined</option>
              <option value="fraud">fraud</option>
              <option value="inventory">inventory</option>
              <option value="other">other</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Status</label>
            <button type="button" onClick={() => saveSetting({ enabled: !(stats.settings?.enabled ?? true) })}
              data-testid="cod-enabled-toggle"
              className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-sm font-bold border ${
                (stats.settings?.enabled ?? true)
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                  : 'bg-slate-100 text-slate-500 border-slate-300'
              }`}>
              <span className={`w-2 h-2 rounded-full ${(stats.settings?.enabled ?? true) ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              {(stats.settings?.enabled ?? true) ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Pending" value={stats.pending} icon={Timer} tone="amber" testId="stat-pending" onClick={() => setFilter('pending')} active={filter === 'pending'} />
        <StatCard label="Confirmed" value={stats.confirmed} icon={CheckCircle2} tone="emerald" testId="stat-confirmed" onClick={() => setFilter('confirmed')} active={filter === 'confirmed'} />
        <StatCard label="Cancelled" value={stats.cancelled} icon={XCircle} tone="rose" testId="stat-cancelled" onClick={() => setFilter('cancelled')} active={filter === 'cancelled'} />
        <StatCard label="All" value={stats.total} icon={Banknote} tone="slate" testId="stat-total" onClick={() => setFilter('')} active={filter === ''} />
      </div>

      {/* Table */}
      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 overflow-hidden">
        <header className="p-5 flex items-center justify-between border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">COD Confirmations {filter && <span className="text-xs font-normal text-slate-500">· filtered: {filter}</span>}</h3>
            <p className="text-xs text-slate-500">Refreshes every 20s</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="h-9"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </header>
        {loading ? (
          <p className="p-10 text-center text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="p-10 text-center space-y-2">
            <Banknote className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-sm text-slate-500">No COD confirmations yet. Place a COD order on Shopify to trigger one.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Order</th>
                <th className="text-left px-5 py-3 font-medium">Phone</th>
                <th className="text-left px-5 py-3 font-medium">Amount</th>
                <th className="text-left px-5 py-3 font-medium">Sent</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Reply</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const ageHrs = sinceHrs(it.sent_at);
                return (
                  <tr key={it.order_id} data-testid={`cod-row-${it.order_id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-semibold text-slate-900">{it.order_name || `#${it.order_id}`}</td>
                    <td className="px-5 py-3 text-slate-600">+{it.phone}</td>
                    <td className="px-5 py-3 text-slate-700 font-mono">{it.amount}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{fmtTime(it.sent_at)} <span className="text-slate-400">· {ageHrs}h ago</span></td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-bold ${STATUS_PILL[it.status] || ''}`}>{it.status}</span></td>
                    <td className="px-5 py-3 text-xs text-slate-500 italic max-w-[160px] truncate" title={it.confirmed_reply || ''}>{it.confirmed_reply || '—'}</td>
                    <td className="px-5 py-3 text-right">
                      {it.status === 'pending' && (
                        <button type="button" onClick={() => manualCancel(it.order_id)} data-testid={`cod-cancel-${it.order_id}`}
                          className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-xs font-semibold">
                          <XCircle className="w-3.5 h-3.5" /> Cancel now
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, testId, onClick, active }) {
  const TONES = {
    amber: 'from-amber-50 to-amber-100 text-amber-700 ring-amber-300',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700 ring-emerald-300',
    rose: 'from-rose-50 to-rose-100 text-rose-700 ring-rose-300',
    slate: 'from-slate-50 to-slate-100 text-slate-700 ring-slate-300',
  };
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-2xl bg-gradient-to-br ${TONES[tone]} border-2 p-5 text-left transition-all hover:-translate-y-0.5 ${
        active ? `ring-2 ${TONES[tone].split(' ').pop()}` : 'border-slate-200'
      }`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5" />
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</span>
      </div>
      <p className="text-3xl font-bold">{value}</p>
    </button>
  );
}

